// Generic property service that handles multiple platforms

import { fetchBazarakiData } from './bazarakiService';
import { fetchBuySellData, detectBuySellPropertyType } from './buysellService';
import { fetchDomComData, detectDomComPropertyType } from './domcomService';
import { fetchAltiaData, detectAltiaPropertyType } from './altiaService';
import { fetchRemuData, detectRemuPropertyType } from './remuService';
import { extractPropertyInfo, PLATFORMS } from '../utils/propertyPlatforms';

/**
 * Fetch property data from any supported platform.
 * All platform services return the normalized data shape defined in propertyDataSchema.js.
 * @param {string} url - Property URL
 * @returns {Promise<Object>} - Normalized property data
 */
export const fetchPropertyData = async (url) => {
  const { platform, isValid } = extractPropertyInfo(url);
  
  if (!isValid) {
    throw new Error('Unsupported property URL format. Supported sites: Bazaraki, BuySell Cyprus, DOM.com.cy, Altia Marketplace, REMU.');
  }

  switch (platform) {
    case PLATFORMS.BAZARAKI:
      return await fetchBazarakiData(url);
    
    case PLATFORMS.BUYSELL:
      return await fetchBuySellData(url);
    
    case PLATFORMS.DOMCOM:
      return await fetchDomComData(url);
    
    case PLATFORMS.ALTIA:
      return await fetchAltiaData(url);
    
    case PLATFORMS.REMU:
      return await fetchRemuData(url);
    
    case PLATFORMS.FACEBOOK:
      // Facebook Marketplace scraping would be implemented here
      throw new Error('Facebook Marketplace support coming soon');
    
    default:
      throw new Error(`Platform ${platform} not supported yet`);
  }
};

/**
 * Validate if a URL is from a supported platform
 * @param {string} url - Property URL to validate
 * @returns {boolean}
 */
export const isValidPropertyUrl = (url) => {
  const { isValid } = extractPropertyInfo(url);
  return isValid;
};

/**
 * Get platform-specific property type detection.
 * Since all services now return normalized data with forSale/forRent,
 * this function can use the normalized data directly or fall back to URL detection.
 * @param {string} url - Property URL
 * @param {Object} normalizedData - Normalized data (already contains forSale/forRent)
 * @returns {Object} - { forSale, forRent }
 */
export const detectPropertyType = (url, normalizedData = null) => {
  // If we have normalized data with type info, use it directly
  if (normalizedData && (normalizedData.forSale !== undefined || normalizedData.forRent !== undefined)) {
    const forSale = normalizedData.forSale || false;
    const forRent = normalizedData.forRent || false;
    if (forSale || forRent) {
      return { forSale, forRent };
    }
  }

  const { platform } = extractPropertyInfo(url);
  
  switch (platform) {
    case PLATFORMS.BAZARAKI: {
      // Use existing Bazaraki detection logic
      const { detectPropertyType: bazarakiDetect } = require('./bazarakiService');
      return bazarakiDetect(url, normalizedData);
    }
    
    case PLATFORMS.BUYSELL:
      return detectBuySellPropertyType(url, normalizedData);
    
    case PLATFORMS.DOMCOM:
      return detectDomComPropertyType(url, normalizedData);
    
    case PLATFORMS.ALTIA:
      return detectAltiaPropertyType(url, normalizedData);
    
    case PLATFORMS.REMU:
      return detectRemuPropertyType(url, normalizedData);
    
    case PLATFORMS.FACEBOOK:
      return { forSale: true, forRent: false };
    
    default:
      // Default fallback - try URL pattern detection
      return detectFromUrl(url);
  }
};

/**
 * Generic URL-based property type detection
 * @param {string} url - Property URL
 * @returns {Object} - { forSale, forRent }
 */
const detectFromUrl = (url) => {
  const urlLower = (url || '').toLowerCase();
  let forSale = false;
  let forRent = false;

  if (urlLower.includes('for-sale') || urlLower.includes('for sale') || urlLower.includes('/sale/')) {
    forSale = true;
  }
  if (urlLower.includes('for-rent') || urlLower.includes('for rent') || urlLower.includes('to-rent') || urlLower.includes('/rent/')) {
    forRent = true;
  }
  if (!forSale && !forRent) {
    forSale = true;
  }

  return { forSale, forRent };
};
